import {Injectable, OnDestroy} from '@angular/core';
import {forkJoin, map, mergeMap, Observable, of, ReplaySubject, Subject, take, takeUntil} from 'rxjs';
import {
  AppConfig,
  AppFullKey,
  IntentConfig,
  IntentFullKey,
  IntentLegacyFullKey,
  IntentVersion,
  WidgetConfig,
} from '../../app-view/api/v1/types';
import {ConfigService} from './config.service';
import {PortalConfigService} from './portal-config.service';
// OpenAPI Shell Service
import {
  ContextService as ShellApiContextService,
  FeatureFlagContextDTO,
  EulaContextDTO,
  BrandingContextDTO,
} from '../../openapi/shell-service-openapi';

// Re-export generierte DTOs für Abwärtskompatibilität (mit alten Namen als Aliase)
export {
  FeatureFlagContextDTO,
  FeatureFlagContextDTO as FeatureFlag,
  EulaContextDTO,
  EulaContextDTO as Eula,
  BrandingContextDTO,
  BrandingContextDTO as Branding,
} from '../../openapi/shell-service-openapi';



export class Apps {

  constructor(
    public appConfigs: AppConfig[],
  ) {
  }

  get appConfigMap(): Map<AppFullKey, AppConfig> {
    return new Map(this.appConfigs.map(app => [app.fullKey, app]));
  }

  get appConfigNavigator(): AppConfig[] {
    return this.appConfigs.filter(app => app.navigator);
  }

  get appConfigEmbedded(): AppConfig[] {
    return this.appConfigs.filter(app => app.embedded);
  }

  get appConfigEmbeddedMap(): Map<AppFullKey, AppConfig> {
    return new Map(this.appConfigEmbedded.map(app => [app.fullKey, app]));
  }
}

export class Widgets {

  constructor(
    public widgetConfigs: WidgetConfig[],
  ) {
  }
}

export class Intents {

  readonly intentVersionConfigs = new Map<IntentFullKey, Map<IntentVersion, IntentConfig>>();

  readonly intentVersionConfigsLegacy = new Map<IntentLegacyFullKey, Map<IntentVersion, IntentConfig>>();

  constructor(
    public intentConfigs: IntentConfig[],
  ) {
    for (const intentConfig of intentConfigs) {
      let versionConfigs = this.intentVersionConfigs.get(intentConfig.fullKey);
      if (!versionConfigs) {
        versionConfigs = new Map();

        this.intentVersionConfigs.set(intentConfig.fullKey, versionConfigs);
      }
      versionConfigs.set(intentConfig.version, intentConfig);
    }
    for (const intentConfig of intentConfigs) {
      let versionConfigs = this.intentVersionConfigsLegacy.get(intentConfig.legacyFullKey);
      if (!versionConfigs) {
        versionConfigs = new Map();

        this.intentVersionConfigsLegacy.set(intentConfig.legacyFullKey, versionConfigs);
      }
      versionConfigs.set(intentConfig.version, intentConfig);
    }
  }
}

export interface IContextConfig {
  apps: AppConfig[],
  intents: IntentConfig[],
  widgets: WidgetConfig[],
  featureFlag: FeatureFlagContextDTO,
  eula: EulaContextDTO,
  branding?: BrandingContextDTO,
}

export class ContextConfig {

  constructor(
    public apps: Apps,
    public intents: Intents,
    public widgets: Widgets,
    public featureFlag: FeatureFlagContextDTO,
    public eula: EulaContextDTO,
    public branding?: BrandingContextDTO,
  ) {
  }
}


@Injectable()
export class ContextConfigService
  implements OnDestroy {

  private readonly unsubscribe$ = new Subject<void>();

  private readonly loaded = new ReplaySubject<ContextConfig>();

  private contextConfig_?: ContextConfig;

  constructor(
    private configService: ConfigService,
    private portalConfigService: PortalConfigService,
    private shellApiContextService: ShellApiContextService,
  ) {
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();

    this.loaded.complete();
  }

  get loaded$() {
    return this.loaded.pipe(
      takeUntil(this.unsubscribe$),
    );
  }

  get contextConfig(): ContextConfig {
    return this.contextConfig_ as ContextConfig;
  }

  get apps(): Apps {
    return this.contextConfig_?.apps as Apps;
  }

  get intents(): Intents {
    return this.contextConfig_?.intents as Intents;
  }

  get widgets(): Widgets {
    return this.contextConfig_?.widgets as Widgets;
  }

  get featureFlag(): FeatureFlagContextDTO {
    return this.contextConfig_?.featureFlag as FeatureFlagContextDTO;
  }

  loadConfig(
    requiredValues: Observable<any>,
  ): Observable<ContextConfig> {
    // INFO: load config only once
    if (!this.contextConfig_) {
      return forkJoin([
        this.getContextConfigs(requiredValues),
      ]).pipe(
        map(value => {
          // INFO: load config only once
          const contextConfig = value[0];
          if (!this.contextConfig_) {
            this.contextConfig_ = contextConfig;

            this.loaded.next(this.contextConfig_);
          }
          return this.contextConfig_;
        }),
      );
    }
    return of(this.contextConfig_);
  }

  private getContextConfigs(
    requiredValues: Observable<any>,
  ): Observable<ContextConfig> {
    return this.configService.loaded$
      .pipe(
        take(1),
        mergeMap(value => {
          return requiredValues.pipe(
            map(() => value),
          );
        }),
        mergeMap(() => this.shellApiContextService.readContext()),
        map(value => {
          // INFO: Archiv-Seite wird clientseitig als System-App registriert,
          // da sie nicht vom Backend geliefert wird (für alle Nutzer verfügbar).
          // Im Gewerbeportal wird sie bewusst NICHT registriert (SHEL-81): Ein
          // Direktaufruf der Archiv-URL läuft dann über die bestehende
          // "unbekannte App"-Behandlung auf die "Seite nicht gefunden"-Ansicht -
          // die einfachste Lösung ohne zusätzlichen Redirect-Code.
          const apps = [...value.apps as AppConfig[]];
          if (!this.portalConfigService.isGewerbeportal
            && !apps.some(app => app.fullKey === 'system:announcement-archive')) {
            apps.push({
              vendorKey: 'system',
              appKey: 'announcement-archive',
              fullKey: 'system:announcement-archive',
              origin: '',
              url: '',
              name: 'Neuigkeiten und Infos',
              system: true,
              navigator: false,
              embedded: false,
              closeable: true,
              autoClose: true,
            });
          }
          return new ContextConfig(
            new Apps(apps),
            new Intents(value.intents as IntentConfig[]),
            new Widgets(value.widgets as WidgetConfig[]),
            value.featureFlag,
            value.eula,
            value.branding,
          );
        }),
      );
  }
}
